import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button, Modal, Row, Col, Form, Tab, Tabs, Alert, Spinner } from 'react-bootstrap';
import { QRCodeSVG } from 'qrcode.react';
import axios from 'axios';
import { useAuth } from "../AuthContext";

const API_URL = process.env.REACT_APP_API_URL;
const FRONTEND_URL = window.location.origin;

const Dashboard = () => {
    const [surveys, setSurveys] = useState([]);
    const [showShareModal, setShowShareModal] = useState(false);
    const [shareLink, setShareLink] = useState('');
    const [currentSurvey, setCurrentSurvey] = useState(null);
    const [emails, setEmails] = useState('');
    const [sendingInvitations, setSendingInvitations] = useState(false);
    const [invitationResult, setInvitationResult] = useState(null);
    const { user } = useAuth();

    const fetchSurveys = async () => {
        try {
            const idToken = await user.getIdToken();
            const response = await axios.get(`${API_URL}/api/surveys`, {
                headers: {
                    Authorization: `Bearer ${idToken}`
                }
            });
            if (response.status === 200) {
                setSurveys(response.data.data);
            }
        } catch (error) {
            console.error("Error fetching surveys:", error);
        }
    };

    useEffect(() => {
        fetchSurveys();
    }, [user]);

    const handleShare = (survey) => {
        const link = `${FRONTEND_URL}/survey/${survey.shortcode}`;
        setShareLink(link);
        setCurrentSurvey(survey);
        setShowShareModal(true);
    };
    const handleDelete = async (survey) => {
        const idToken = await user.getIdToken();
        await axios.delete(
            `${API_URL}/api/surveys/${survey._id}`,
            {
                headers: {
                    Authorization: `Bearer ${idToken}`
                }
            }
        );
        fetchSurveys()
    };

    const copyToClipboard = async () => {
        try {
            await navigator.clipboard.writeText(shareLink);
            alert('Link copied to clipboard!');
        } catch (err) {
            console.error('Failed to copy link:', err);
        }
    };

    const downloadQRCode = () => {
        const canvas = document.getElementById('qr-code');
        if (canvas) {
            const pngUrl = canvas
                .toDataURL("image/png")
                .replace("image/png", "image/octet-stream");
            const downloadLink = document.createElement("a");
            downloadLink.href = pngUrl;
            downloadLink.download = `${currentSurvey.title}-qr.png`;
            document.body.appendChild(downloadLink);
            downloadLink.click();
            document.body.removeChild(downloadLink);
        }
    };

    const handleSendInvitations = async () => {
        if (!emails.trim()) {
            alert('Please enter at least one email address');
            return;
        }

        try {
            setSendingInvitations(true);
            setInvitationResult(null);

            // Split the emails by commas, newlines, or semicolons and trim whitespace
            const emailList = emails
                .split(/[,;\n]/) // Split by comma, semicolon, or newline
                .map(email => email.trim())
                .filter(email => email); // Remove empty strings

            // Basic email validation
            const invalidEmails = emailList.filter(email => {
                return !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
            });

            if (invalidEmails.length > 0) {
                alert(`Invalid email(s): ${invalidEmails.join(', ')}`);
                setSendingInvitations(false);
                return;
            }

            const idToken = await user.getIdToken();
            const response = await axios.post(
                `${API_URL}/api/surveys/${currentSurvey.shortcode}/invitations`,
                { emailAddresses: emailList },
                {
                    headers: {
                        Authorization: `Bearer ${idToken}`
                    }
                }
            );

            setInvitationResult({
                success: true,
                sent: response.data.data.sent.length,
                failed: response.data.data.failed.length,
                total: response.data.data.total
            });
        } catch (error) {
            console.error('Error sending invitations:', error);
            setInvitationResult({
                success: false,
                message: error.response?.data?.error || 'Failed to send invitations'
            });
        } finally {
            setSendingInvitations(false);
        }
    };

    return (
        <div>
            <h2>My Workspace</h2>
            <Link to="/create" className="btn btn-primary mb-3">
                + Create New Survey
            </Link>
            <div className="table-responsive">
                <table className="table table-bordered table-hover">
                    <thead className="table-dark">
                    <tr>
                        <th>#</th>
                        <th>Survey Title</th>
                        <th>Actions</th>
                    </tr>
                    </thead>
                    <tbody>
                    {surveys.length > 0 ? (
                        surveys.map((survey, index) => (
                            <tr key={survey._id}>
                                <th scope="row">{index + 1}</th>
                                <td>{survey.title}</td>
                                <td>
                                    <div className="d-flex gap-2">
                                        <Link
                                            to={`/configure/${survey._id}`}
                                            className="btn btn-primary btn-sm"
                                        >
                                            Edit
                                        </Link>
                                        <Link
                                            to={`/survey/${survey.shortcode}`}
                                            className="btn btn-secondary btn-sm"
                                        >
                                            Preview
                                        </Link>
                                        <Link
                                            to={`/analyze/${survey._id}`}
                                            className="btn btn-info btn-sm"
                                        >
                                            Analysis
                                        </Link>
                                        <Button
                                            variant="success"
                                            size="sm"
                                            onClick={() => handleShare(survey)}
                                        >
                                            Share
                                        </Button>
                                        <Button
                                            variant="danger"
                                            size="sm"
                                            onClick={() => handleDelete(survey)}
                                        >
                                            Delete
                                        </Button>
                                    </div>
                                </td>
                            </tr>
                        ))
                    ) : (
                        <tr>
                            <td colSpan="3" className="text-center">
                                No surveys available. Create a new survey to get started!
                            </td>
                        </tr>
                    )}
                    </tbody>
                </table>
            </div>

            {/* Share Modal with Tabs */}
            <Modal 
                show={showShareModal} 
                onHide={() => {
                    setShowShareModal(false);
                    setEmails('');
                    setInvitationResult(null);
                }}
                size="lg"
            >
                <Modal.Header closeButton>
                    <Modal.Title>Share Survey: {currentSurvey?.title}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Tabs defaultActiveKey="link" id="share-tabs" className="mb-3">
                        <Tab eventKey="link" title="Share Link">
                            <Row>
                                <Col md={7}>
                                    <p>Share this link with participants:</p>
                                    <div className="input-group mb-3">
                                        <input
                                            type="text"
                                            className="form-control"
                                            value={shareLink}
                                            readOnly
                                        />
                                        <button
                                            className="btn btn-outline-secondary"
                                            type="button"
                                            onClick={copyToClipboard}
                                        >
                                            Copy
                                        </button>
                                    </div>
                                </Col>
                                <Col md={5} className="text-center">
                                    <p>Or scan QR code:</p>
                                    <QRCodeSVG
                                        id="qr-code"
                                        value={shareLink}
                                        size={128}
                                        level="H"
                                        includeMargin={true}
                                    />
                                    <div className="mt-2">
                                        <Button
                                            variant="outline-primary"
                                            size="sm"
                                            onClick={downloadQRCode}
                                        >
                                            Download QR Code
                                        </Button>
                                    </div>
                                </Col>
                            </Row>
                        </Tab>
                        <Tab eventKey="email" title="Send Invitations">
                            <Form>
                                <Form.Group className="mb-3">
                                    <Form.Label>Email Addresses</Form.Label>
                                    <Form.Control
                                        as="textarea"
                                        rows={4}
                                        placeholder="Enter email addresses (separated by commas, semicolons, or new lines)"
                                        value={emails}
                                        onChange={(e) => setEmails(e.target.value)}
                                        disabled={sendingInvitations}
                                    />
                                    <Form.Text className="text-muted">
                                        We'll send personalized survey invitations with unique tracking links.
                                    </Form.Text>
                                </Form.Group>
                                
                                {invitationResult && (
                                    <Alert 
                                        variant={invitationResult.success ? 'success' : 'danger'}
                                        className="mt-3"
                                    >
                                        {invitationResult.success ? (
                                            <>
                                                <Alert.Heading>Invitations Sent!</Alert.Heading>
                                                <p>
                                                    Successfully sent {invitationResult.sent} invitation(s).
                                                    {invitationResult.failed > 0 && (
                                                        <> Failed to send {invitationResult.failed} invitation(s).</>
                                                    )}
                                                </p>
                                            </>
                                        ) : (
                                            <>
                                                <Alert.Heading>Error Sending Invitations</Alert.Heading>
                                                <p>{invitationResult.message}</p>
                                            </>
                                        )}
                                    </Alert>
                                )}
                                
                                <Button 
                                    variant="primary" 
                                    onClick={handleSendInvitations}
                                    disabled={sendingInvitations || !emails.trim()}
                                >
                                    {sendingInvitations ? (
                                        <>
                                            <Spinner
                                                as="span"
                                                animation="border"
                                                size="sm"
                                                role="status"
                                                aria-hidden="true"
                                                className="me-2"
                                            />
                                            Sending...
                                        </>
                                    ) : 'Send Invitations'}
                                </Button>
                            </Form>
                        </Tab>
                    </Tabs>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => {
                        setShowShareModal(false);
                        setEmails('');
                        setInvitationResult(null);
                    }}>
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default Dashboard;